import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, mediaSrcById } from "../api";
import { useApp } from "../context/AppContext";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { AvatarLightbox } from "./AvatarLightbox";
import { PdfViewer } from "./PdfViewer";
import { toast } from "sonner";
import { MessageSquare, FileText, Users as UsersIcon, Download, Play } from "lucide-react";

const isPdf = (md) => md?.content_type === "application/pdf" || /\.pdf$/i.test(md?.filename || "");

function dayLabel(day, lang, t) {
  const todayD = new Date();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (day === fmt(todayD)) return t("today");
  const y = new Date(todayD); y.setDate(y.getDate() - 1);
  if (day === fmt(y)) return t("yesterday");
  const [yy, mm, dd] = day.split("-").map(Number);
  return new Intl.DateTimeFormat(lang, { weekday: "long", day: "numeric", month: "long", year: "numeric" }).format(new Date(yy, mm - 1, dd));
}

/** Image / video / pdf / file opened in-app (never a new tab). */
function MediaModal({ item, onClose, t }) {
  if (!item) return null;
  const { media, name } = item;
  const url = mediaSrcById(media.id);
  if (media.kind === "image" || media.kind === "photo") {
    return <AvatarLightbox src={url} name={name} onClose={onClose} />;
  }
  if (isPdf(media)) {
    return <PdfViewer open media={media} onClose={onClose} />;
  }
  const isVideo = media.kind === "video";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={`${isVideo ? "max-w-3xl bg-black border-0 p-2 pt-10" : "max-w-3xl h-[85vh] flex flex-col"}`} data-testid="admin-media-modal">
        <DialogHeader className={isVideo ? "sr-only" : ""}>
          <DialogTitle className="text-sm truncate pr-6">{media.filename || name}</DialogTitle>
        </DialogHeader>
        {isVideo ? (
          <video src={url} controls autoPlay playsInline className="w-full max-h-[75vh] rounded-md bg-black" data-testid="admin-media-video" />
        ) : (
          <>
            <iframe src={url} title={media.filename || "file"} className="flex-1 w-full rounded-md border bg-muted/30" data-testid="admin-media-iframe" />
            <a href={url} download={media.filename || "file"} className="self-end" data-testid="admin-media-download">
              <Button size="sm" variant="outline" className="gap-1.5"><Download className="h-4 w-4" />{media.filename || t("download")}</Button>
            </a>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MessageRow({ m, usersMap, onOpenMedia, t }) {
  const su = usersMap[m.sender_id];
  const senderName = su?.name || m.sender_name || "?";
  const mediaList = m.is_deleted ? [] : (m.media || []);
  return (
    <div className={`rounded-lg border p-2 ${m.mirrored ? "opacity-60" : ""}`} data-testid="admin-conv-msg">
      <div className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 min-w-0">
          <span className="h-5 w-5 rounded-full shrink-0 overflow-hidden flex items-center justify-center text-[9px] font-bold text-white" style={{ backgroundColor: su?.avatar_color || "#64748b" }}>
            {su?.avatar_media_id
              ? <img src={mediaSrcById(su.avatar_media_id)} alt="" className="h-full w-full object-cover" />
              : senderName.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
          </span>
          <span className="text-xs font-semibold text-primary truncate">{senderName}</span>
        </span>
        <span className="text-[10px] text-muted-foreground shrink-0">
          {m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
        </span>
      </div>
      {mediaList.length > 0 && (
        <div className="mt-1.5 space-y-1.5">
          {mediaList.map((md) => {
            const url = mediaSrcById(md.id);
            const open = () => onOpenMedia({ media: md, name: senderName });
            if (md.kind === "image" || md.kind === "photo") {
              return <img key={md.id} src={url} alt="" loading="lazy" data-testid="admin-msg-image" className="rounded-lg max-h-44 border cursor-pointer object-cover" onClick={open} />;
            }
            if (md.kind === "video") {
              return (
                <button key={md.id} type="button" onClick={open} data-testid="admin-msg-video" className="relative block w-full rounded-lg border bg-black overflow-hidden">
                  <video src={url} preload="metadata" muted playsInline className="max-h-44 w-full pointer-events-none" />
                  <span className="absolute inset-0 flex items-center justify-center"><span className="h-11 w-11 rounded-full bg-white/90 flex items-center justify-center"><Play className="h-5 w-5 text-black ml-0.5" /></span></span>
                </button>
              );
            }
            if (md.kind === "audio" || md.kind === "voice" || m.type === "voice") {
              return <audio key={md.id} src={url} controls preload="none" data-testid="admin-msg-audio" className="w-full h-9" />;
            }
            return (
              <button key={md.id} type="button" onClick={open} data-testid="admin-msg-file" className="text-xs text-primary underline flex items-center gap-1 text-left">
                <FileText className="h-3.5 w-3.5 shrink-0" />{md.filename || md.kind || "file"}
              </button>
            );
          })}
        </div>
      )}
      <div className="text-sm mt-0.5 break-words">
        {m.is_deleted
          ? <em className="text-muted-foreground">({t("message_deleted")})</em>
          : (m.content || (mediaList.length === 0 ? <em className="text-muted-foreground">[{m.type}]</em> : null))}
      </div>
    </div>
  );
}

/** MASTER-only audit: every conversation split per calendar day, read-only viewer. */
export function AdminConversationsTab({ t }) {
  const { lang } = useApp();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [person, setPerson] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [usersMap, setUsersMap] = useState({});
  const [mediaItem, setMediaItem] = useState(null);

  useEffect(() => {
    api.get("/admin/users").then((res) => {
      const m = {};
      (res.data || []).forEach((u) => { m[u.id] = u; });
      setUsersMap(m);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    api.get(`/admin/conversation-days?tz=${encodeURIComponent(tz)}`)
      .then((res) => setEntries(res.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Error"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDay = (e) => {
    setViewing(e);
    setMsgs(null);
    api.get(`/conversations/${e.id}/messages?after=${encodeURIComponent(e.day_start)}&before=${encodeURIComponent(e.day_end)}&limit=2000`)
      .then((res) => setMsgs(res.data))
      .catch(() => setMsgs([]));
  };

  const people = useMemo(() => Object.values(usersMap).sort((a, b) => (a.name || "").localeCompare(b.name || "")), [usersMap]);

  const ql = q.trim().toLowerCase();
  const filtered = entries.filter((e) =>
    (person === "all" || (e.participant_ids || []).includes(person)) &&
    (!ql ||
      (e.label || "").toLowerCase().includes(ql) ||
      (e.participants || []).some((p) => (p || "").toLowerCase().includes(ql)) ||
      (e.job_number || "").includes(ql) ||
      (e.last_message?.content || "").toLowerCase().includes(ql)));

  const groups = useMemo(() => {
    const out = [];
    let cur = null;
    filtered.forEach((e) => {
      if (!cur || cur.day !== e.day) { cur = { day: e.day, items: [] }; out.push(cur); }
      cur.items.push(e);
    });
    return out;
  }, [filtered]);

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input data-testid="admin-conv-search" placeholder={t("search")} value={q} onChange={(e) => setQ(e.target.value)} className="h-10 flex-1" />
        <Select value={person} onValueChange={setPerson}>
          <SelectTrigger className="h-10 w-[180px] sm:w-[220px]" data-testid="admin-conv-person">
            <UsersIcon className="h-4 w-4 mr-1.5 shrink-0 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("all_people")}</SelectItem>
            {people.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {loading && <div className="text-sm text-muted-foreground py-8 text-center">…</div>}
      {!loading && filtered.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">—</div>}
      {groups.map((g) => (
        <div key={g.day} className="space-y-2" data-testid="admin-conv-day-group">
          <div className="sticky top-0 z-10 bg-background/95 backdrop-blur py-1.5 flex items-center gap-2">
            <span className="text-xs font-display font-semibold uppercase tracking-wide text-muted-foreground" data-testid="admin-conv-day-label">{dayLabel(g.day, lang, t)}</span>
            <span className="h-px flex-1 bg-border" />
            <span className="text-[11px] text-muted-foreground tabular-nums">{g.items.reduce((s, e) => s + e.message_count, 0)} msg</span>
          </div>
          {g.items.map((e) => (
            <Card key={`${e.id}-${e.day}`} className="p-3 cursor-pointer hover:bg-muted/50 transition-colors" data-testid="admin-conv-row" onClick={() => openDay(e)}>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary shrink-0" />
                <span className="font-semibold text-sm truncate flex-1">{e.label}</span>
                <Badge variant="secondary" className="text-[10px] uppercase shrink-0">{e.type}</Badge>
                <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">{e.message_count} msg</span>
              </div>
              <div className="text-xs text-muted-foreground truncate mt-0.5">{(e.participants || []).join(", ")}</div>
              {e.last_message && (
                <div className="text-xs text-muted-foreground truncate mt-0.5">
                  <span className="font-medium">{e.last_message.sender_name}:</span>{" "}
                  {e.last_message.content || `[${e.last_message.type}]`}
                </div>
              )}
            </Card>
          ))}
        </div>
      ))}

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] flex flex-col" data-testid="admin-conv-viewer">
          <DialogHeader>
            <DialogTitle className="font-display text-base truncate pr-6">{viewing?.label}</DialogTitle>
            {viewing && <div className="text-xs text-muted-foreground" data-testid="admin-conv-viewer-day">{dayLabel(viewing.day, lang, t)} · {viewing.message_count} msg</div>}
          </DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1 min-h-[200px]">
            {msgs === null && <div className="text-sm text-muted-foreground py-8 text-center">…</div>}
            {msgs !== null && msgs.length === 0 && <div className="text-sm text-muted-foreground py-8 text-center">—</div>}
            {(msgs || []).map((m) => <MessageRow key={m.id} m={m} usersMap={usersMap} onOpenMedia={setMediaItem} t={t} />)}
          </div>
        </DialogContent>
      </Dialog>
      <MediaModal item={mediaItem} onClose={() => setMediaItem(null)} t={t} />
    </div>
  );
}
